'use client';
import { useEffect } from 'react';
import { Box, Typography, Button, Container, Card, CardContent, Grid, Paper, Chip, Divider } from '@mui/material';
import { Business, Psychology, TrendingUp, Group, Security, Speed, CheckCircle, Email, Phone } from '@mui/icons-material';
import TopNavigation from '@/components/TopNavigation';
import Footer from '@/components/Footer';
import Link from 'next/link';

export default function About() {
  useEffect(() => {
    document.title = 'CareAscendium - About';
  }, []);
  const values = [
    {
      icon: Business,
      title: "Excellence",
      description: "We strive for excellence in everything we do, from our technology to our customer service.",
      color: "#153C7F"
    },
    {
      icon: Psychology,
      title: "Innovation",
      description: "Continuously innovating to solve complex challenges in healthcare technology.",
      color: "#00B4D8"
    },
    {
      icon: Group,
      title: "Collaboration",
      description: "Working together with our clients to create solutions that truly make a difference.",
      color: "#28A745"
    },
    {
      icon: Security,
      title: "Trust",
      description: "Building trust through transparency, reliability, and consistent delivery.",
      color: "#6F42C1"
    }
  ];

  const milestones = [
    {
      year: "2020",
      title: "Company Founded",
      description: "CareAscendium was established with a vision to transform skilled nursing facility operations."
    },
    {
      year: "2021",
      title: "First Product Launch",
      description: "Successfully launched our initial QAPI management platform to select facilities."
    },
    {
      year: "2022",
      title: "Market Expansion",
      description: "Expanded to serve over 100 facilities across multiple states."
    },
    {
      year: "2023",
      title: "AI Integration",
      description: "Introduced advanced AI-powered features for predictive analytics and automation."
    },
    {
      year: "2024",
      title: "Industry Leader",
      description: "Recognized as a leading provider of healthcare technology solutions."
    }
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <TopNavigation />

      {/* Hero Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 50%, #2A5A9C 100%)',
        py: 12, 
        px: 4,
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography 
            variant="h1" 
            sx={{ 
              color: 'white', 
              mb: 4,
              fontWeight: 800,
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              textShadow: '0 2px 4px rgba(0,0,0,0.3)'
            }}
          >
            About CareAscendium
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 6,
              color: 'rgba(255,255,255,0.9)',
              maxWidth: '900px',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.2rem', md: '1.5rem' }
            }}
          >
            Transforming skilled nursing facility operations through innovative technology and unwavering commitment to quality care.
          </Typography>
        </Container>
      </Box>

      {/* Vision & Mission Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: 'linear-gradient(45deg, #153C7F, #1E4A8C)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 3,
                  boxShadow: '0 4px 15px rgba(21,60,127,0.3)'
                }}>
                  <Business sx={{ fontSize: 30, color: 'white' }} />
                </Box>
                <Typography variant="h2" sx={{ color: '#153C7F', fontWeight: 700 }}>
                  Our Vision
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ mb: 4, lineHeight: 1.6, color: '#333' }}>
                To be the leading technology partner for skilled nursing facilities, empowering them to deliver exceptional care through innovative solutions.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Chip label="Quality Care" sx={{ bgcolor: '#153C7F15', color: '#153C7F', fontWeight: 600 }} />
                <Chip label="Operational Excellence" sx={{ bgcolor: '#153C7F15', color: '#153C7F', fontWeight: 600 }} />
                <Chip label="Human-Centered" sx={{ bgcolor: '#153C7F15', color: '#153C7F', fontWeight: 600 }} />
              </Box>
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, md: 6 }}>
            <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  background: 'linear-gradient(45deg, #00B4D8, #0099CC)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 3,
                  boxShadow: '0 4px 15px rgba(0,180,216,0.3)'
                }}>
                  <Psychology sx={{ fontSize: 30, color: 'white' }} />
                </Box>
                <Typography variant="h2" sx={{ color: '#00B4D8', fontWeight: 700 }}>
                  Our Mission
                </Typography>
              </Box>
              <Typography variant="h5" sx={{ mb: 4, lineHeight: 1.6, color: '#333' }}>
                To provide cutting-edge technology solutions that streamline operations, enhance compliance, and improve resident outcomes in skilled nursing facilities.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                <Chip label="Innovation" sx={{ bgcolor: '#00B4D815', color: '#00B4D8', fontWeight: 600 }} />
                <Chip label="Efficiency" sx={{ bgcolor: '#00B4D815', color: '#00B4D8', fontWeight: 600 }} />
                <Chip label="Compliance" sx={{ bgcolor: '#00B4D815', color: '#00B4D8', fontWeight: 600 }} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Values Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
        py: 10 
      }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="h2" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
              Our Core Values
            </Typography>
            <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
              The principles that guide everything we do at CareAscendium
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <Paper 
                  elevation={6}
                  sx={{ 
                    p: 4,
                    textAlign: 'center',
                    height: '100%',
                    borderRadius: 3,
                    background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                    border: `1px solid ${value.color}20`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 32px ${value.color}30`
                    }
                  }}
                >
                  <Box sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: `linear-gradient(45deg, ${value.color}, ${value.color}80)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: `0 4px 15px ${value.color}40`
                  }}>
                    <value.icon sx={{ fontSize: 40, color: 'white' }} />
                  </Box>
                  
                  <Typography variant="h4" sx={{ color: value.color, mb: 2, fontWeight: 700 }}>
                    {value.title}
                  </Typography>
                  
                  <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                    {value.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Company Story Section */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h2" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
            Our Journey
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: '#333', fontSize: '1.1rem' }}>
            From startup to industry leader - the milestones that shaped our success
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {milestones.map((milestone, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Paper 
                elevation={4}
                sx={{ 
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid rgba(21,60,127,0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(21,60,127,0.15)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #153C7F, #1E4A8C)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 3,
                    boxShadow: '0 4px 15px rgba(21,60,127,0.3)'
                  }}>
                    <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
                      {milestone.year}
                    </Typography>
                  </Box>
                  <Typography variant="h3" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
                    {milestone.title}
                  </Typography>
                </Box>
                
                <Typography variant="h5" sx={{ color: '#153C7F', mb: 3, fontWeight: 600 }}>
                  {milestone.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Contact Information Section */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%)',
        py: 10 
      }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                <Typography variant="h2" sx={{ color: '#153C7F', mb: 4, fontWeight: 700 }}>
                  Get in Touch
                </Typography>
                <Typography variant="h6" sx={{ mb: 6, lineHeight: 1.6, color: '#333' }}>
                  Ready to transform your skilled nursing facility operations? Let's discuss how CareAscendium can help you achieve your goals.
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Email sx={{ fontSize: 24, color: '#153C7F', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#153C7F', fontWeight: 600 }}>
                      info@careascendium.com
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Phone sx={{ fontSize: 24, color: '#153C7F', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#153C7F', fontWeight: 600 }}>
                      (555) 123-4567
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Business sx={{ fontSize: 24, color: '#153C7F', mr: 2 }} />
                    <Typography variant="h6" sx={{ color: '#153C7F', fontWeight: 600 }}>
                      CareAscendium Headquarters
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper 
                elevation={8}
                sx={{ 
                  p: 4,
                  borderRadius: 3,
                  background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
                  border: '1px solid rgba(21,60,127,0.1)'
                }}
              >
                <Box sx={{ 
                  background: 'linear-gradient(45deg, #153C7F, #1E4A8C)',
                  color: 'white',
                  p: 3,
                  borderRadius: 2,
                  mb: 3,
                  textAlign: 'center'
                }}>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Request a Demo
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    See CareAscendium in action
                  </Typography>
                </Box>
                
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6, mb: 3 }}>
                  Schedule a personalized demonstration of our platform and discover how we can help your facility improve operations, enhance compliance, and deliver better resident care.
                </Typography>
                
                <Button 
                  variant="contained" 
                  size="large"
                  sx={{ 
                    background: 'linear-gradient(45deg, #153C7F, #1E4A8C)',
                    color: 'white',
                    px: 4,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 15px rgba(21,60,127,0.3)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #0F2A5A, #153C7F)',
                      boxShadow: '0 6px 20px rgba(21,60,127,0.4)',
                      transform: 'translateY(-2px)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Schedule Demo
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ 
        py: 12, 
        background: 'linear-gradient(135deg, #153C7F 0%, #1E4A8C 100%)',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.1,
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 2px, transparent 2px), radial-gradient(circle at 75% 75%, white 2px, transparent 2px)',
          backgroundSize: '50px 50px'
        }} />
        
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ mb: 4, fontWeight: 800, fontSize: { xs: '2rem', md: '3rem' } }}>
            Ready to Get Started?
          </Typography>
          <Typography variant="h5" sx={{ mb: 6, opacity: 0.9, lineHeight: 1.6 }}>
            Join hundreds of skilled nursing facilities that have transformed their operations with CareAscendium
          </Typography>
          
          <Link href="/contact" style={{ textDecoration: 'none' }}>
            <Button 
              variant="contained" 
              size="large"
              startIcon={<Group />}
              sx={{ 
                bgcolor: 'white',
                color: '#153C7F',
                px: 6,
                py: 3,
                fontSize: '1.2rem',
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                boxShadow: '0 4px 15px rgba(255,255,255,0.3)',
                '&:hover': {
                  bgcolor: '#f5f5f5',
                  boxShadow: '0 6px 20px rgba(255,255,255,0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Contact Us Today
            </Button>
          </Link>
        </Container>
      </Box>
      <Footer />
    </Box>
  );
} 